import Cookies, { CookieAttributes } from 'js-cookie';
import { CookieKeys, StorageKeys } from '../types/keys.type';
import { AccessToken, IdentityToken } from '../types/auth.type';
import decode from 'jwt-decode';
import ddLogger from '../logging/ddLogger';
import { TranslationUtils } from './translation.utils';
import { UserState } from '../types/user.store.type';
import { LanguageCodes } from '../types/preference.type';
import { ValidationUtils } from './validation.utils';

export const memoryStorage: { [key: string]: any } = {};

export const defaultTimeZone: string = 'America/Los_Angeles';

export class SharedUtils {
    public static setCookie(key: CookieKeys, value: string | object, options?: CookieAttributes): void {
        Cookies.set(key, value, options);
    }

    public static getCookie(key: CookieKeys): string | undefined {
        return Cookies.get(key);
    }

    public static setSessionStorageItem(key: string, item: any): void {
        try {
            sessionStorage.setItem(key, item);
            memoryStorage[key] = item;
        } catch (e) {
            ddLogger.error(`ERROR setting ${key} sessionStorageItem: ${e.message}`);
        }
    }

    public static getSessionStorageItem(key: string): string | null {
        return sessionStorage.getItem(key) || memoryStorage[key];
    }

    public static removeSessionStorageItem(key: string) {
        sessionStorage.removeItem(key);
        memoryStorage[key] = null;
    }

    public static clearSessionStorage() {
        sessionStorage.clear();
        for (const key of Object.keys(memoryStorage)) {
            memoryStorage[key] = null;
        }
    }

    public static getDecodedAccessToken(): AccessToken | null {
        const accessToken = SharedUtils.getSessionStorageItem(StorageKeys.ACCESS_TOKEN);
        if (accessToken) {
            const decodedToken = SharedUtils.decodeToken(accessToken);
            return decodedToken ? (decodedToken as AccessToken) : null;
        }

        return null;
    }

    public static getDecodedIdentityToken(): IdentityToken | null {
        const identityToken = SharedUtils.getSessionStorageItem(StorageKeys.IDENTITY_TOKEN);
        if (identityToken) {
            const decodedToken = SharedUtils.decodeToken(identityToken);
            return decodedToken ? (decodedToken as IdentityToken) : null;
        }

        return null;
    }

    public static decodeToken(token: string): object | null {
        try {
            return decode(token);
        } catch (error) {
            return null;
        }
    }

    public static isBrowserFirefox(): boolean {
        return navigator.userAgent.toLowerCase().indexOf('firefox') > -1;
    }

    public static getSlugOccupancy(key: string, value: number): string {
        let slugOccupancy = '';
        switch (key) {
            case 'adults':
                slugOccupancy =
                    value === 1
                        ? `${value} ${TranslationUtils.get('payment_form_adult')} `
                        : `${value} ${TranslationUtils.get('payment_form_adults')} `;
                break;
            case 'children':
                slugOccupancy =
                    value === 1
                        ? `${value} ${TranslationUtils.get('payment_form_child')}`
                        : `${value} ${TranslationUtils.get('payment_form_children')}`;
                break;
            case 'pets':
                slugOccupancy = value === 1 ? `${value} ${TranslationUtils.get('pet')}` : `${value} ${TranslationUtils.get('pets')}`;
                break;
        }
        return slugOccupancy;
    }

    public static getUserLanguageCode(user: UserState): string {
        const languageCode = user?.language?.code ?? '';

        return languageCode === LanguageCodes.ES_LA ? 'es' : languageCode;
    }

    public static getFormattedTollFreePhoneNumber(): string {
        const phoneNumber = ValidationUtils.validateAndGetEnvVar('REACT_APP_TOLL_FREE_PHONE_NUMBER');
        const phoneNumberWithoutPlus = phoneNumber.replace('+', '');
        return phoneNumberWithoutPlus.replace(/(\d)(\d{3})(\d{3})(\d{4})/, '$1-$2-$3-$4');
    }
}
